import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import {
  useGetPromotionQuery,
  useCreatePromotionConditionMutation,
  useUpdatePromotionConditionMutation,
  useDeletePromotionConditionMutation,
  useCreatePromotionEffectMutation,
  useUpdatePromotionEffectMutation,
  useDeletePromotionEffectMutation,
  useCreatePromotionScheduleMutation,
  useUpdatePromotionScheduleMutation,
  useDeletePromotionScheduleMutation,
} from "../../store/api";

const dayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const emptyCondition = { id: null, minAmount: "" };
const emptyEffect = {
  id: null,
  effectType: "PERCENT",
  value: "",
  maxDiscountAmount: "",
};
const emptySchedule = {
  id: null,
  dayOfWeek: "",
  startTime: "",
  endTime: "",
  isActive: true,
};
const SHOW_WEEKLY_SCHEDULE = false;

const optionalNumber = (value) =>
  value === "" || value === null || value === undefined ? null : Number(value);

const errorText = (error) =>
  error?.data?.detail || error?.data?.title || "Unable to save promotion rules.";

const RuleRow = ({ title, detail, onEdit, onDelete, disabled }) => (
  <div className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <div className="font-semibold text-slate-900">{title}</div>
      <div className="mt-1 text-sm text-slate-500">{detail}</div>
    </div>
    <div className="flex gap-2">
      <Button type="button" size="sm" variant="secondary" onClick={onEdit}>
        Edit
      </Button>
      <Button
        type="button"
        size="sm"
        variant="danger"
        onClick={onDelete}
        disabled={disabled}
      >
        Delete
      </Button>
    </div>
  </div>
);

const PromotionRulesEditor = ({ storeId, promotionId, onBack }) => {
  const { data, isLoading, error } = useGetPromotionQuery(
    { storeId, promotionId },
    { skip: !storeId || !promotionId },
  );
  const [condition, setCondition] = useState(emptyCondition);
  const [effect, setEffect] = useState(emptyEffect);
  const [schedule, setSchedule] = useState(emptySchedule);
  const [notice, setNotice] = useState("");

  const [createCondition, createConditionState] =
    useCreatePromotionConditionMutation();
  const [updateCondition, updateConditionState] =
    useUpdatePromotionConditionMutation();
  const [deleteCondition, deleteConditionState] =
    useDeletePromotionConditionMutation();
  const [createEffect, createEffectState] = useCreatePromotionEffectMutation();
  const [updateEffect, updateEffectState] = useUpdatePromotionEffectMutation();
  const [deleteEffect, deleteEffectState] = useDeletePromotionEffectMutation();
  const [createSchedule, createScheduleState] =
    useCreatePromotionScheduleMutation();
  const [updateSchedule, updateScheduleState] =
    useUpdatePromotionScheduleMutation();
  const [deleteSchedule, deleteScheduleState] =
    useDeletePromotionScheduleMutation();

  const conditions = data?.conditions || [];
  const effects = data?.effects || [];
  const schedules = data?.schedules || [];
  const promotion = data?.promotion;
  const mutationError =
    createConditionState.error ||
    updateConditionState.error ||
    deleteConditionState.error ||
    createEffectState.error ||
    updateEffectState.error ||
    deleteEffectState.error ||
    createScheduleState.error ||
    updateScheduleState.error ||
    deleteScheduleState.error;
  const isMutating = [
    createConditionState,
    updateConditionState,
    deleteConditionState,
    createEffectState,
    updateEffectState,
    deleteEffectState,
    createScheduleState,
    updateScheduleState,
    deleteScheduleState,
  ].some((state) => state.isLoading);

  const saveCondition = async (event) => {
    event.preventDefault();
    if (!(Number(condition.minAmount) > 0)) return;
    const payload = {
      conditionType: "MIN_ORDER_AMOUNT",
      itemId: null,
      categoryId: null,
      tagId: null,
      proteinTypeId: null,
      bundleId: null,
      minQty: null,
      minAmount: Number(condition.minAmount),
      valueText: null,
    };
    try {
      if (condition.id) {
        await updateCondition({
          storeId,
          promotionId,
          conditionId: condition.id,
          payload,
        }).unwrap();
      } else {
        await createCondition({ storeId, promotionId, payload }).unwrap();
      }
      setCondition(emptyCondition);
      setNotice("Minimum order condition saved.");
    } catch {
      setNotice("");
    }
  };

  const saveEffect = async (event) => {
    event.preventDefault();
    if (!(Number(effect.value) > 0)) return;
    const payload = {
      effectType: effect.effectType,
      targetScope: "ORDER",
      itemId: null,
      categoryId: null,
      tagId: null,
      bundleId: null,
      valuePercent: effect.effectType === "PERCENT" ? Number(effect.value) : null,
      valueAmount: effect.effectType === "AMOUNT" ? Number(effect.value) : null,
      setPrice: null,
      freeQty: null,
      maxDiscountAmount: optionalNumber(effect.maxDiscountAmount),
    };
    try {
      if (effect.id) {
        await updateEffect({
          storeId,
          promotionId,
          effectId: effect.id,
          payload,
        }).unwrap();
      } else {
        await createEffect({ storeId, promotionId, payload }).unwrap();
      }
      setEffect(emptyEffect);
      setNotice("Discount effect saved.");
    } catch {
      setNotice("");
    }
  };

  const saveSchedule = async (event) => {
    event.preventDefault();
    const payload = {
      dayOfWeek: schedule.dayOfWeek === "" ? null : Number(schedule.dayOfWeek),
      startTime: schedule.startTime || null,
      endTime: schedule.endTime || null,
      isActive: schedule.isActive,
    };
    try {
      if (schedule.id) {
        await updateSchedule({
          storeId,
          promotionId,
          scheduleId: schedule.id,
          payload,
        }).unwrap();
      } else {
        await createSchedule({ storeId, promotionId, payload }).unwrap();
      }
      setSchedule(emptySchedule);
      setNotice("Schedule saved.");
    } catch {
      setNotice("");
    }
  };

  const removeCondition = async (id) => {
    if (!window.confirm("Delete this minimum order condition?")) return;
    await deleteCondition({ storeId, promotionId, conditionId: id });
  };
  const removeEffect = async (id) => {
    if (!window.confirm("Delete this discount effect?")) return;
    await deleteEffect({ storeId, promotionId, effectId: id });
  };
  const removeSchedule = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    await deleteSchedule({ storeId, promotionId, scheduleId: id });
  };

  if (isLoading) return <Card>Loading promotion rules...</Card>;
  if (error) return <Alert tone="error">{errorText(error)}</Alert>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            {promotion?.name || "Promotion"} rules
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Configure the minimum spend, order discount, and availability schedule used by POS.
          </p>
        </div>
        <Button type="button" variant="secondary" onClick={onBack}>
          Back to promotions
        </Button>
      </div>

      {notice && <Alert tone="success">{notice}</Alert>}
      {mutationError && <Alert tone="error">{errorText(mutationError)}</Alert>}

      <Card>
        <h3 className="text-lg font-semibold text-slate-900">Minimum order</h3>
        <p className="mt-1 text-sm text-slate-500">
          The POS backend currently enforces minimum order amount conditions.
        </p>
        <form className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={saveCondition}>
          <Input
            label="Minimum subtotal"
            type="number"
            min="0.01"
            step="0.01"
            value={condition.minAmount}
            onChange={(event) =>
              setCondition((current) => ({ ...current, minAmount: event.target.value }))
            }
            containerClassName="flex-1"
            required
          />
          <Button type="submit" disabled={isMutating || !(Number(condition.minAmount) > 0)}>
            {condition.id ? "Update condition" : "Add condition"}
          </Button>
          {condition.id && (
            <Button type="button" variant="secondary" onClick={() => setCondition(emptyCondition)}>
              Cancel
            </Button>
          )}
        </form>
        <div className="mt-4 space-y-2">
          {conditions.length === 0 && <p className="text-sm text-slate-500">No minimum order condition.</p>}
          {conditions.map((item) => (
            <RuleRow
              key={item.id}
              title={item.conditionType === "MIN_ORDER_AMOUNT" ? "Minimum order amount" : item.conditionType}
              detail={item.minAmount != null ? `Subtotal must be at least ${Number(item.minAmount).toFixed(2)}` : "Configured condition"}
              onEdit={() => setCondition({ id: item.id, minAmount: item.minAmount ?? "" })}
              onDelete={() => removeCondition(item.id)}
              disabled={isMutating}
            />
          ))}
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-slate-900">Order discount</h3>
        <p className="mt-1 text-sm text-slate-500">
          Add a percentage or fixed amount effect. At least one order effect is required before POS can apply the promotion.
        </p>
        <form className="mt-4 grid gap-4 md:grid-cols-2" onSubmit={saveEffect}>
          <Select
            label="Discount type"
            value={effect.effectType}
            onChange={(event) => setEffect((current) => ({ ...current, effectType: event.target.value }))}
          >
            <option value="PERCENT">Percentage</option>
            <option value="AMOUNT">Fixed amount</option>
          </Select>
          <Input
            label={effect.effectType === "PERCENT" ? "Percentage" : "Amount"}
            type="number"
            min="0.01"
            max={effect.effectType === "PERCENT" ? 100 : undefined}
            step="0.01"
            value={effect.value}
            onChange={(event) => setEffect((current) => ({ ...current, value: event.target.value }))}
            required
          />
          <Input
            label="Maximum discount (optional)"
            type="number"
            min="0.01"
            step="0.01"
            value={effect.maxDiscountAmount}
            onChange={(event) => setEffect((current) => ({ ...current, maxDiscountAmount: event.target.value }))}
          />
          <div className="flex items-end gap-3">
            <Button type="submit" disabled={isMutating || !(Number(effect.value) > 0)}>
              {effect.id ? "Update effect" : "Add effect"}
            </Button>
            {effect.id && (
              <Button type="button" variant="secondary" onClick={() => setEffect(emptyEffect)}>
                Cancel
              </Button>
            )}
          </div>
        </form>
        <div className="mt-4 space-y-2">
          {effects.length === 0 && <Alert tone="warning">Add an order discount effect before using this promotion in POS.</Alert>}
          {effects.map((item) => {
            const value = item.effectType === "PERCENT" ? item.valuePercent : item.valueAmount;
            return (
              <RuleRow
                key={item.id}
                title={item.effectType === "PERCENT" ? `${value}% off` : `${Number(value || 0).toFixed(2)} off`}
                detail={`Applies to ${String(item.targetScope || "ORDER").toLowerCase()}${item.maxDiscountAmount != null ? ` · maximum ${Number(item.maxDiscountAmount).toFixed(2)}` : ""}`}
                onEdit={() => setEffect({
                  id: item.id,
                  effectType: item.effectType,
                  value: item.effectType === "PERCENT" ? item.valuePercent ?? "" : item.valueAmount ?? "",
                  maxDiscountAmount: item.maxDiscountAmount ?? "",
                })}
                onDelete={() => removeEffect(item.id)}
                disabled={isMutating}
              />
            );
          })}
        </div>
      </Card>

      {SHOW_WEEKLY_SCHEDULE && <Card>
        <h3 className="text-lg font-semibold text-slate-900">Weekly schedule</h3>
        <p className="mt-1 text-sm text-slate-500">
          Leave the schedule empty to allow the promotion throughout its overall start and end dates.
        </p>
        <form className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4" onSubmit={saveSchedule}>
          <Select
            label="Day"
            value={schedule.dayOfWeek}
            onChange={(event) => setSchedule((current) => ({ ...current, dayOfWeek: event.target.value }))}
          >
            <option value="">Every day</option>
            {dayNames.map((day, index) => <option key={day} value={index}>{day}</option>)}
          </Select>
          <Input label="Start time" type="time" value={schedule.startTime} onChange={(event) => setSchedule((current) => ({ ...current, startTime: event.target.value }))} />
          <Input label="End time" type="time" value={schedule.endTime} onChange={(event) => setSchedule((current) => ({ ...current, endTime: event.target.value }))} />
          <div className="flex items-end gap-3 pb-1">
            <Checkbox label="Active" checked={schedule.isActive} onChange={(event) => setSchedule((current) => ({ ...current, isActive: event.target.checked }))} />
          </div>
          <div className="flex gap-3 lg:col-span-4">
            <Button type="submit" disabled={isMutating}>{schedule.id ? "Update schedule" : "Add schedule"}</Button>
            {schedule.id && <Button type="button" variant="secondary" onClick={() => setSchedule(emptySchedule)}>Cancel</Button>}
          </div>
        </form>
        <div className="mt-4 space-y-2">
          {schedules.length === 0 && <p className="text-sm text-slate-500">Available every day and time within the promotion dates.</p>}
          {schedules.map((item) => (
            <RuleRow
              key={item.id}
              title={item.dayOfWeek == null ? "Every day" : dayNames[item.dayOfWeek]}
              detail={`${item.startTime || "Start of day"} – ${item.endTime || "End of day"} · ${item.isActive ? "Active" : "Inactive"}`}
              onEdit={() => setSchedule({ id: item.id, dayOfWeek: item.dayOfWeek ?? "", startTime: item.startTime || "", endTime: item.endTime || "", isActive: item.isActive })}
              onDelete={() => removeSchedule(item.id)}
              disabled={isMutating}
            />
          ))}
        </div>
      </Card>}
    </div>
  );
};

export default PromotionRulesEditor;
